/**
 * useShareFloatingNotification: 다른 사용자의 화면/문서/스케치 공유 시 상단 알림 표시
 *
 * - 화면공유 / 문서공유 / 스케치모드 공유를 감지하여 GlobalNotification으로 표시
 * - "전환" 클릭 → 공유 화면으로 전환 (PRESENTER/SIDE + pin)
 * - "×" 클릭 → 닫기 (새 공유자가 등장할 때까지 재표시 안 함)
 * - GRID 모드에서는 항상 표시, SIDE/PRESENTER에서는 공유 화면을 보고 있지 않을 때만 표시
 *
 * 기획: 다른 사용자가 화면/문서 공유한 경우 (② 케이스)
 */
import { useEffect, useRef, useMemo, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import { showNotification, dismissNotification } from '@/utils/globalToast';
import { parsePinKey } from '@/utils/pinUtils';

// ── 공유자 정보 ──────────────────────────────────────────────────────────

interface SharerInfo {
  /** 핀 대상 ID (화면공유 가상 참여자 ID 또는 문서/스케치 오너 ID) */
  id: string;
  /** 표시용 이름 */
  name: string;
  /** 공유 유형 */
  type: 'screen' | 'document' | 'sketch';
}

// ── 공유 유형별 텍스트 ──────────────────────────────────────────────────

function getShareTypeText(sharers: SharerInfo[]): string {
  if (sharers.length === 0) return '';

  const types = new Set(sharers.map((s) => s.type));
  // 여러 유형이 섞이면 통합 표현
  if (types.size > 1) return '화면·문서 공유 중';

  switch (sharers[0].type) {
    case 'screen':
      return '화면 공유 중';
    case 'document':
      return '문서 공유 중';
    case 'sketch':
      return '스케치모드 공유 중';
    default:
      return '화면 공유 중';
  }
}

// ── 훅 본체 ─────────────────────────────────────────────────────────────

export function useShareFloatingNotification(): void {
  const dispatch = useAppDispatch();
  const participants = useAppSelector((s) => s.participants);
  const documentShare = useAppSelector((s) => s.documentShare);
  const drawing = useAppSelector((s) => s.drawing);
  const screenMode = useAppSelector((s) => s.ui.screenMode);
  const mainUser = useAppSelector((s) => s.mainUser);
  const localJitsiId = useAppSelector((s) => s.local.jitsiId);

  const notificationIdRef = useRef<string | null>(null);

  // × 클릭 시점의 공유자 ID 목록 (새 공유자가 등장하면 초기화)
  const [dismissedSharerIds, setDismissedSharerIds] = useState<string[]>([]);

  // ── 공유자 목록 구성 ────────────────────────────────────────────────

  const sharers = useMemo<SharerInfo[]>(() => {
    const list: SharerInfo[] = [];
    const addedIds = new Set<string>();

    // 1) 화면공유 참여자 (tracks.isScreenSharing === true)
    for (const id of participants.participantIds) {
      const p = participants.participants[id];
      if (p && p.tracks.isScreenSharing) {
        const name = (p.displayName || p.nickname || '').trim();
        if (name) {
          list.push({ id, name, type: 'screen' });
          addedIds.add(id);
        }
      }
    }

    // 2) 문서공유 (내가 오너가 아닌 경우)
    if (documentShare.isOpen && !documentShare.isOwner && documentShare.ownerId) {
      const ownerId = documentShare.ownerId;
      if (!addedIds.has(ownerId)) {
        const owner = participants.participants[ownerId];
        const name = (owner?.displayName || owner?.nickname || '').trim();
        if (name) {
          list.push({ id: ownerId, name, type: 'document' });
          addedIds.add(ownerId);
        }
      }
    }

    // 3) 스케치모드 공유 (내가 오너가 아닌 경우)
    if (drawing.reduxIsSketchShare && !drawing.reduxIsSketchOwner && drawing.reduxSketchOwner) {
      const ownerId = drawing.reduxSketchOwner;
      if (!addedIds.has(ownerId)) {
        const owner = participants.participants[ownerId];
        const name = (owner?.displayName || owner?.nickname || '').trim();
        if (name) {
          list.push({ id: ownerId, name, type: 'sketch' });
          addedIds.add(ownerId);
        }
      }
    }

    return list;
  }, [participants, documentShare, drawing, localJitsiId]);

  // ── 공유 유형 텍스트 ────────────────────────────────────────────────

  const shareTypeText = useMemo(() => getShareTypeText(sharers), [sharers]);

  // ── 현재 보고 있는 공유 화면 확인 ──────────────────────────────────

  const currentViewingId = mainUser.pinnedUserId ?? mainUser.mainUserId;
  const pinnedShowScreenShare = mainUser.pinnedShowScreenShare;
  const presenterOverrideUserId = useAppSelector((s) => s.ui.presenterOverrideUserId);
  const isShareClick = useAppSelector((s) => s.ui.isShareClick);

  const isViewingSharedContent = useMemo(() => {
    // GRID에서는 특정 공유 화면을 보고 있지 않으므로 false
    if (screenMode === 'GRID') return false;

    // PRESENTER 모드: 실제 표시 화면은 presenterOverrideUserId/isShareClick 등으로 결정됨
    if (screenMode === 'PRESENTER') {
      // 더블클릭으로 특정 타일을 강제 선택한 경우 → 해당 타일이 공유화면인지 확인
      if (presenterOverrideUserId !== null) {
        const { userId: oId, isScreenShare: oSS } = parsePinKey(presenterOverrideUserId);
        return oSS && sharers.some((s) => s.id === oId);
      }
      // "전환" 버튼으로 공유 화면을 보고 있는 경우
      if (isShareClick) return true;
      // 그 외: 핀/mainUser 기반
      return sharers.some((s) => s.id === currentViewingId);
    }

    // SIDE 모드: 카메라를 명시적으로 선택한 경우 → 공유 화면을 보고 있지 않음
    if (pinnedShowScreenShare === false) return false;
    return sharers.some((s) => s.id === currentViewingId);
  }, [screenMode, sharers, currentViewingId, pinnedShowScreenShare, presenterOverrideUserId, isShareClick]);

  // ── 표시할 이름 (현재 보고 있는 공유자는 ㅇㅇㅇ 위치에 안 오도록) ──

  const displayName = useMemo(() => {
    if (sharers.length === 0) return '';
    const notViewing = sharers.filter((s) => s.id !== currentViewingId);
    return notViewing.length > 0 ? notViewing[0].name : sharers[0].name;
  }, [sharers, currentViewingId]);

  // ── 표시 여부 결정 ──────────────────────────────────────────────────

  const shouldShow = useMemo(() => {
    if (sharers.length === 0) return false;
    if (isViewingSharedContent) return false;

    // × 클릭 후 새 공유자가 없으면 표시 안 함
    if (
      dismissedSharerIds.length > 0 &&
      !sharers.some((s) => !dismissedSharerIds.includes(s.id))
    ) {
      return false;
    }

    return true;
  }, [sharers, isViewingSharedContent, dismissedSharerIds]);

  // ── 새 공유자 등장 시 dismissed 상태 초기화 ──────────────────────────

  useEffect(() => {
    if (sharers.length === 0) {
      if (dismissedSharerIds.length > 0) {
        setDismissedSharerIds([]);
      }
      return;
    }

    if (
      dismissedSharerIds.length > 0 &&
      sharers.some((s) => !dismissedSharerIds.includes(s.id))
    ) {
      setDismissedSharerIds([]);
    }
  }, [sharers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 최신 상태를 참조하는 ref (콜백 안정성 확보) ──────────────────────

  const latestRef = useRef({ sharers, currentViewingId, dispatch });
  latestRef.current = { sharers, currentViewingId, dispatch };

  // ── 알림 표시/갱신 ──────────────────────────────────────────────────

  const notificationText = useMemo(() => {
    if (!displayName || !shareTypeText) return '';

    return sharers.length > 1
      ? `${displayName}님 외 ${sharers.length - 1}명이 ${shareTypeText}`
      : `${displayName}님이 ${shareTypeText}`;
  }, [displayName, shareTypeText, sharers.length]);

  const textKey = shouldShow ? notificationText : '';

  useEffect(() => {
    if (!shouldShow || !textKey) {
      // 숨김
      if (notificationIdRef.current) {
        dismissNotification(notificationIdRef.current);
        notificationIdRef.current = null;
      }
      return;
    }

    // 텍스트가 같으면 기존 알림 유지
    if (notificationIdRef.current) {
      // 텍스트가 바뀌면 갱신
      dismissNotification(notificationIdRef.current);
      notificationIdRef.current = null;
    }

    const id = showNotification({
      text: notificationText,
      buttons: [
        {
          label: '전환',
          variant: 'blue',
          onClick: () => {
            const { sharers: s, currentViewingId: vid, dispatch: d } = latestRef.current;
            if (s.length === 0) return;

            // 현재 보고 있는 공유자의 다음 순서로 전환, 없으면 첫 번째로
            const idx = s.findIndex((sh) => sh.id === vid);
            const next = idx !== -1 && idx < s.length - 1 ? s[idx + 1] : s[0];

            // 이전 더블클릭(presenterOverrideUserId)이 남아 있으면 isShareClick보다
            // 우선순위가 높아 직전 선택 화면이 유지되므로 반드시 초기화
            d(uiActions.setPresenterOverrideUserId(null));

            if (next.type === 'screen') {
              d(uiActions.setScreenMode('PRESENTER'));
              d(mainUserActions.pinUser(next.id));
              d(uiActions.setIsShareClick(true));
            } else {
              // 문서/스케치: 사이드뷰로 전환
              d(uiActions.setScreenMode('SIDE'));
              d(mainUserActions.pinUser(next.id));
            }
          },
        },
        {
          label: '×',
          onClick: () => {
            const { sharers: s } = latestRef.current;
            setDismissedSharerIds(s.map((sh) => sh.id));
            if (notificationIdRef.current) {
              dismissNotification(notificationIdRef.current);
              notificationIdRef.current = null;
            }
          },
        },
      ],
      duration: 0,
      autoDismissOnClick: false,
    });
    notificationIdRef.current = id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, textKey]);

  // ── 클린업 ──────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      if (notificationIdRef.current) {
        dismissNotification(notificationIdRef.current);
        notificationIdRef.current = null;
      }
    };
  }, []);
}
